import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Maximize2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ChartContent, type ViewMode } from './chart-content';
import { ChartExpandModal } from './chart-expand-modal';

// Re-export ViewMode for external consumers
export type { ViewMode };

// Define component props
interface DailyRatesChartProps {
	viewMode?: ViewMode;
}

export function DailyRatesChart({ viewMode = 'daily' }: DailyRatesChartProps) {
	// Dialog state management
	const [isModalOpen, setIsModalOpen] = useState(false);

	const { startDate, endDate } = useMemo(() => {
		const today = new Date();
		// Format the date as YYYY-MM-DD by truncating the time portion
		today.setHours(0, 0, 0, 0);

		const start = new Date(today);

		const end = new Date(today);
		end.setDate(today.getDate() + 120); // Set to today + 120 days

		return { startDate: start, endDate: end };
	}, []); // Empty deps array since we only need to calculate this once

	// Dynamic title based on the view mode
	const chartTitle =
		viewMode === 'daily'
			? 'Daily Rate Recommendations'
			: 'Avg Daily Rate Recommendations';

	// Dynamic description based on the view mode
	const chartDescription =
		viewMode === 'daily'
			? `Daily rates (${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()})`
			: `By day of week (${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()})`;

	// Handler function for expand button click
	const handleExpandClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		setIsModalOpen(true);
	};

	// Handler function for modal open/close state changes
	const handleModalOpenChange = (open: boolean) => {
		setIsModalOpen(open);
	};

	return (
		<>
			<Card>
				<CardHeader className='tw-px-2'>
					<div className='tw-flex tw-items-start tw-justify-between'>
						<div className='tw-flex tw-flex-col tw-space-y-1.5'>
							<CardTitle>{chartTitle}</CardTitle>
							<CardDescription>{chartDescription}</CardDescription>
						</div>
						<Button
							variant='ghost'
							size='sm'
							className='tw-h-8 tw-w-8 tw-p-0 tw-text-muted-foreground hover:tw-text-foreground'
							aria-label='Expand chart'
							onClick={handleExpandClick}
						>
							<Maximize2 className='tw-h-4 tw-w-4' />
						</Button>
					</div>
				</CardHeader>
				<CardContent className='tw-px-2'>
					<ChartContent viewMode={viewMode} />
				</CardContent>
			</Card>

			<ChartExpandModal
				isOpen={isModalOpen}
				onOpenChange={handleModalOpenChange}
				viewMode={viewMode}
			/>
		</>
	);
}
